import {
    OnboardingStatus,
    type User,
    type UserAddress,
} from '../../user/types/User';
import { isEmpty, properCase } from '../misc';

export const formatFullName = (
    firstName?: string | null,
    lastName?: string | null,
): string => {
    return `${properCase(firstName ?? '').trim()} ${properCase(
        lastName ?? '',
    ).trim()}`.trim();
};

export const getUserInitials = (
    firstName?: string | null,
    lastName?: string | null,
): string | null => {
    return firstName != null && lastName != null
        ? `${firstName?.slice(0, 1)}${lastName?.slice(0, 1)}`
        : null;
};

export const formatFullNameFallbackEmail = (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    email: string | null | undefined,
): string => {
    const fullName = formatFullName(firstName, lastName);
    if (fullName === '') {
        return email ?? '';
    } else {
        return fullName;
    }
};

/** Partner first name for UI; null/blank-after-trim uses fallback. */
export const getPartnerDisplayFirstName = (
    firstName: string | null | undefined,
    fallback: string = 'Your partner',
): string => {
    const trimmed = firstName?.trim();
    return trimmed != null && trimmed !== '' ? trimmed : fallback;
};

/** Partner full name for UI; empty formatted name uses fallback. */
export const getPartnerDisplayFullName = (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    fallback: string = 'Your partner',
): string => {
    const fullName = formatFullName(firstName, lastName);
    return fullName !== '' ? fullName : fallback;
};

export const formatPhoneNumber = (
    phoneNumber?: string | null,
    excludeCountryCode: boolean = false,
): string => {
    if (phoneNumber == null) {
        return '';
    }

    // Remove non-numeric characters from the input string
    const numericString = phoneNumber.replace(/\D/g, '');

    // If number starts with 1 and has 11 digits, it has a country code
    const hasCountryCode =
        numericString.length === 11 && numericString.startsWith('1');
    const core = hasCountryCode ? numericString.slice(1) : numericString;

    if (core.length !== 10) {
        return phoneNumber; // not a standard US number, return as-is
    }

    // Use regular expressions to format the numeric string
    const formattedCore = core.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');

    if (hasCountryCode && !excludeCountryCode) {
        return `+1 ${formattedCore}`;
    }

    return formattedCore;
};

export const formatSsn = (ssn?: string | null): string => {
    if (ssn == null) return '';
    return ssn.replace(/\D/g, '').replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
};

export const formatSsnWithMask = (ssn?: string | null): string => {
    const formattedSsn = formatSsn(ssn);
    if (formattedSsn === '') return '';

    const lastFour = formattedSsn.slice(-4);
    return formattedSsn.replace(/\d/g, '*').slice(0, -4) + lastFour;
};

export const isActiveMember = (user?: User): boolean => {
    return user?.membership != null && !!user?.isSubscriptionActive;
};

const COMPLETED_MEMBERSHIP_ONBOARDING_STATUSES: OnboardingStatus[] = [
    OnboardingStatus.COMPLETED,
    OnboardingStatus.WELCOME_CALL,
    OnboardingStatus.JOINT_COMPLETED,
];

/**
 * A person whose membership has ended (cancelled, unpaid, etc.) but who still
 * has a Fruitful account. Signup drop-offs are not included — they never had
 * an active membership and should not hit the former-member document portal.
 */
export const isFormerMember = (user?: User): boolean => {
    if (user == null || !!user.isSubscriptionActive) {
        return false;
    }

    return (
        user.membership != null ||
        COMPLETED_MEMBERSHIP_ONBOARDING_STATUSES.includes(user.onboardingStatus)
    );
};

export const getUserGuideName = (user: User): string => {
    const { membership } = user ?? {};
    const { guide } = membership ?? {};
    return guide?.firstName ?? 'your Guide';
};

export const formatMailingAddress = ({
    line1,
    line2,
    city,
    state,
    postalCode,
}: UserAddress) => {
    return `${line1}${!isEmpty(line2) ? `, ${line2}` : ''}\n${city},${state != null ? ` ${state}` : ''} ${postalCode}`;
};

export const getMembershipFirstNames = (user: User): string => {
    const primaryFirstName = user.firstName;
    if (user.partnerMember == null) {
        return `${primaryFirstName}`;
    }

    return `${primaryFirstName} & ${getPartnerDisplayFirstName(
        user.partnerMember.firstName,
        'Partner',
    )}`;
};

const MONEY_MAPS_LAUNCH_DATE = new Date('2025-05-15');

export const isPreMoneyMapUser = (user: User): boolean => {
    return new Date(user.createdAt) < MONEY_MAPS_LAUNCH_DATE;
};
